import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Account } from '@/domain/accounts/account'
import { Check, CheckSquare, Search, Square } from 'lucide-react'
import { useState } from 'react'

interface MinimalAccountSelectorProps {
  accounts: Account[]
  selectedIds: string[]
  onToggle: (id: string) => void
  onSelectAll: () => void
  onClearAll: () => void
}

export const MinimalAccountSelector = ({
  accounts,
  selectedIds,
  onToggle,
  onSelectAll,
  onClearAll,
}: MinimalAccountSelectorProps) => {
  const [search, setSearch] = useState('')

  const filtered = accounts.filter(
    (a) =>
      a.accountNumber.toLowerCase().includes(search.toLowerCase()) ||
      (a.accountAlias && a.accountAlias.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <div className="space-y-3 bg-card border rounded-lg p-3">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filtrar cuentas..."
            className="h-8 pl-8 text-xs"
          />
        </div>
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <span className="text-xs text-muted-foreground">
            {selectedIds.length} de {accounts.length} seleccionadas
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onSelectAll}
            className="h-7 text-xs px-2"
          >
            <CheckSquare className="h-3.5 w-3.5 mr-1" />
            Todas
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
          >
            <Square className="h-3.5 w-3.5 mr-1" />
            Ninguna
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto p-1">
        {filtered.length === 0 ? (
          <p className="text-xs text-muted-foreground py-2">No se encontraron cuentas</p>
        ) : (
          filtered.map((acc) => {
            const isSelected = selectedIds.includes(acc.id)
            return (
              <button
                key={acc.id}
                type="button"
                onClick={() => onToggle(acc.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border transition-all ${
                  isSelected
                    ? 'bg-primary/10 border-primary text-primary shadow-xs'
                    : 'bg-background hover:bg-muted border-input text-muted-foreground'
                }`}
              >
                <div
                  className={`w-3.5 h-3.5 rounded flex items-center justify-center border text-[9px] ${
                    isSelected
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-muted-foreground/30'
                  }`}
                >
                  {isSelected && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                </div>
                <span className="font-mono font-semibold">{acc.accountNumber}</span>
                {acc.accountAlias && (
                  <span className="max-w-[130px] truncate text-[11px] opacity-80">
                    {acc.accountAlias}
                  </span>
                )}
                <Badge
                  variant={acc.currentBalance >= 0 ? 'light' : 'destructive'}
                  className="ml-1 text-[10px] py-0 px-1"
                >
                  ${acc.currentBalance.toLocaleString('es-AR')}
                </Badge>
              </button>
            )
          })
        )}
      </div>
    </div>
  )
}

